import { useState } from "react";
import { useGetIdentity, useNotify, useRefresh } from "ra-core";
import { useNavigate } from "react-router";
import { Mic, PhoneCall, Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { appendDictated, dictationSupported, useDictation } from "../misc/dictation";
import { pageContext, sendClaudeRequest } from "../claude/ClaudeChat";
import type { Contact } from "../types";

// LDF: Gespräch mitschreiben (Freisprechen am Rechner) oder direkt danach diktieren.
// Mitschnitt des Gesprächspartners nur mit dessen Einwilligung (§ 201 StGB) – deshalb Pflicht-Häkchen.

export const CallTranscript = ({ contact }: { contact: Contact }) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"diktat" | "gespraech">("diktat");
  const [consent, setConsent] = useState(false);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [startedAt, setStartedAt] = useState<Date | null>(null);
  const { identity } = useGetIdentity();
  const notify = useNotify();
  const refresh = useRefresh();
  const navigate = useNavigate();
  const { listening, interim, error, start, stop } = useDictation((t) => setText((v) => appendDictated(v, t)));

  const name = `${contact.academic_title ?? ""} ${contact.first_name ?? ""} ${contact.last_name ?? ""}`.replace(/\s+/g, " ").trim();

  const begin = () => {
    if (!startedAt) setStartedAt(new Date());
    start();
  };

  const header = () => {
    const when = (startedAt ?? new Date()).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" });
    return mode === "gespraech"
      ? `Gesprächsmitschrift mit ${name}, ${when} (Einwilligung zur Mitschrift liegt vor)`
      : `Gesprächsnotiz zu ${name}, ${when}`;
  };

  const saveNote = async () => {
    stop();
    setSaving(true);
    const now = new Date().toISOString();
    const { error: insertError } = await getSupabaseClient()
      .from("contact_notes")
      .insert({ contact_id: contact.id, text: `${header()}\n\n${text.trim()}`, date: now, sales_id: identity?.id });
    if (!insertError) await getSupabaseClient().from("contacts").update({ last_seen: now }).eq("id", contact.id);
    setSaving(false);
    if (insertError) return notify(`Speichern fehlgeschlagen: ${insertError.message}`, { type: "error" });
    notify("Als Notiz gespeichert (mit Datum und Verfasser)", { type: "success" });
    return true;
  };

  const summarize = async () => {
    const ok = await saveNote();
    if (!ok || !identity) return;
    await sendClaudeRequest({
      threadId: crypto.randomUUID(),
      salesId: identity.id,
      context: { ...pageContext(`/contacts/${contact.id}/show`) },
      prompt: `${header()} (Kontakt #${contact.id}${contact.company_id ? `, Firma #${contact.company_id}` : ""}).\n\nBitte fasse das Gespräch in 3–6 Stichpunkten zusammen (Bedarf, Event/Termin, Budget-Hinweise, nächste Schritte) und schlage passende Aktionen vor: Notiz mit der Zusammenfassung, Folgeaufgabe(n) mit Datum, ggf. Event-Status.\n\nMitschrift:\n${text.trim()}`,
    });
    notify("Claude fasst zusammen – Ergebnis erscheint im Claude-Chat unten rechts", { type: "info" });
    finish();
  };

  const finish = () => {
    stop();
    setOpen(false);
    setText("");
    setConsent(false);
    setStartedAt(null);
    refresh();
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} className="gap-1">
        <PhoneCall className="h-3.5 w-3.5" /> Gespräch mitschreiben
      </Button>
      <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : finish())}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Gespräch mit {name}</DialogTitle>
          </DialogHeader>

          <div className="flex gap-1 rounded-full bg-muted p-1 text-sm w-fit">
            {(
              [
                ["diktat", "Nach dem Gespräch diktieren"],
                ["gespraech", "Gespräch mitschreiben (Freisprechen)"],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setMode(value)}
                className={`rounded-full px-3 py-1 cursor-pointer ${mode === value ? "bg-background shadow-sm font-medium" : "text-muted-foreground"}`}
              >
                {label}
              </button>
            ))}
          </div>

          {mode === "gespraech" && (
            <label className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-sm dark:bg-amber-950">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1" />
              <span>
                <strong>{name}</strong> ist einverstanden, dass das Gespräch mitgeschrieben wird. Ohne Einwilligung ist das Mitschneiden
                fremder Worte strafbar (§ 201 StGB). Tipp: Zu Beginn fragen „Ist es okay, wenn ich mitschreiben lasse?“
              </span>
            </label>
          )}

          {!dictationSupported() && (
            <p className="text-sm text-muted-foreground">
              Spracheingabe wird in diesem Browser nicht unterstützt. Alternative: ins Feld klicken und macOS-Diktat starten (zweimal Fn).
            </p>
          )}

          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant={listening ? "destructive" : "default"}
              disabled={mode === "gespraech" && !consent}
              onClick={listening ? stop : begin}
              className="gap-1"
            >
              <Mic className="h-4 w-4" /> {listening ? "Stopp" : mode === "gespraech" ? "Mitschrift starten" : "Diktat starten"}
            </Button>
            {listening && <span className="text-xs italic text-muted-foreground">{interim || "Hört zu …"}</span>}
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}

          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={10}
            placeholder={mode === "gespraech" ? "Hier erscheint die Mitschrift …" : "Was wurde besprochen? Einfach erzählen …"}
            className="w-full rounded-md border border-input bg-background p-2 text-sm"
          />

          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="ghost" onClick={finish}>
              Verwerfen
            </Button>
            <Button variant="outline" disabled={!text.trim() || saving} onClick={async () => (await saveNote()) && finish()}>
              Als Notiz speichern
            </Button>
            <Button disabled={!text.trim() || saving} onClick={summarize} className="gap-1">
              <Sparkles className="h-4 w-4" /> Speichern + Claude fasst zusammen
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Gespeichert werden Datum, Uhrzeit und wer die Notiz angelegt hat. „Zuletzt kontaktiert“ beim Kontakt wird aktualisiert.
            {" "}
            <button type="button" className="underline cursor-pointer" onClick={() => navigate(`/contacts/${contact.id}/show`)}>
              Zum Kontakt
            </button>
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
};
